import { Separator } from '@/components/ui/separator';
import PopUpBuyer from './PopUpBuyer';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { WebinarPurchaseNotificationConfigInput } from '@/features/live/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchOfferPurchaseNotificationConfig,
  updateOfferPurchaseNotificationConfig,
} from '@/features/live/liveThunks';
import { toast } from 'react-toastify';
import { normalizeAxiosError } from '@/http/errors';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Loader from '@/components/ui/Loader';
import type { RootState } from '@/app/store';
import { cn } from '@/lib/utils';
import { getReadableTextColor } from '@/utils/colors';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  hasValidContent,
  messageTemplateToChunks,
  chunksToMessageTemplate,
  calculateMessageLength,
  isCustomColor,
  mapColorToId,
  isNoBorder,
  resolveColorToHex,
  resolveBackgroundColorForApi,
} from '@/lib/purchaseNotificationForm';

const chunkSchema = z.union([
  z.object({ type: z.literal('text'), value: z.string(), id: z.string() }),
  z.object({ type: z.literal('field'), value: z.string(), id: z.string() }),
]);

const publicVariantSchema = z
  .object({
    id: z.string(),
    chunks: z.array(chunkSchema),
    selectedIcon: z.string(),
    showIcon: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (!hasValidContent(data.chunks)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('createWebinar:popupForm.validation.messageRequired'),
        path: ['chunks'],
      });
    } else if (calculateMessageLength(data.chunks) > 150) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('createWebinar:popupForm.validation.messageMax'),
        path: ['chunks'],
      });
    }
  });

const purchaseNotificationSchema = z
  .object({
    privateChunks: z.array(chunkSchema),
    privateIcon: z.string(),
    displayPrivateIcon: z.boolean(),
    privateBackgroundColor: z.string(),
    customPrivateBackgroundColor: z.string().optional(),
    privateBorderColor: z.string(),
    customPrivateBorderColor: z.string().optional(),
    privateTextColor: z.string(),
    privateBackgroundIcon: z.string(),
    customPrivateBackgroundIcon: z.string().optional(),
    privateIconColor: z.string(),
    customPrivateIconColor: z.string().optional(),
    publicIcon: z.string(),
    displayPublicIcon: z.boolean(),
    publicBackgroundColor: z.string(),
    customPublicBackgroundColor: z.string().optional(),
    publicBorderColor: z.string(),
    customPublicBorderColor: z.string().optional(),
    publicTextColor: z.string(),
    publicBackgroundIcon: z.string(),
    customPublicBackgroundIcon: z.string().optional(),
    publicIconColor: z.string(),
    customPublicIconColor: z.string().optional(),
    publicVariants: z.array(publicVariantSchema).min(1).max(4),
  })
  .superRefine((data, ctx) => {
    if (!hasValidContent(data.privateChunks)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('createWebinar:popupForm.validation.messageRequired'),
        path: ['privateChunks'],
      });
    } else if (calculateMessageLength(data.privateChunks) > 150) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('createWebinar:popupForm.validation.messageMax'),
        path: ['privateChunks'],
      });
    }
  });

export type PurchaseNotificationFormValues = z.infer<typeof purchaseNotificationSchema>;

export type PopUpsSectionRef = {
  buildPurchaseNotificationInput: () => Promise<WebinarPurchaseNotificationConfigInput | null>;
};

interface PopUpsSectionProps {
  visualIdentity: WorkspaceVisualIdentity | null | undefined;
  webinarId?: number;
  workspaceId?: number;
  offerId?: number;
  readOnly?: boolean;
  onSaveSuccess?: () => void;
  hideSaveButton?: boolean;
}

const PopUpsSection = forwardRef<PopUpsSectionRef, PopUpsSectionProps>(function PopUpsSection(
  { visualIdentity, webinarId, workspaceId, offerId, readOnly, onSaveSuccess, hideSaveButton },
  ref,
) {
  const { t } = useTranslation('createWebinar');
  const locked = readOnly === true;
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [initializedWebinarId, setInitializedWebinarId] = useState<number | undefined>(undefined);

  // Une seule config par offre (config.288), partagée par les deux variantes A/B (pas de notif par
  // variante). Le back a son propre repli sur la config legacy du webinaire pour les offres sans
  // config dédiée (cf. IntegrationWebinarServiceImpl.findNotificationConfiguration), mais ce repli
  // ne concerne que la notif réellement envoyée à l'achat — pas ce formulaire, qui édite toujours
  // la config de l'offre affichée.
  const purchaseNotificationConfig = useAppSelector((state: RootState) =>
    offerId != null ? state.live.offerPurchaseNotificationConfigs[offerId] : undefined,
  );
  const purchaseNotificationConfigLoading = useAppSelector((state: RootState) =>
    offerId != null ? (state.live.offerPurchaseNotificationConfigLoading[offerId] ?? false) : false,
  );

  // Les deux notifications (post-conversion privée + preuve sociale publique) prennent
  // l'accent comme fond par défaut : elles ressortent ainsi de l'interface, qui est
  // blanche en base claire (un fond `background` blanc se fondait dedans). On dérive
  // ensuite un texte noir/blanc lisible sur cet accent selon sa luminance.
  const defaultNotificationTextColor = getReadableTextColor(
    visualIdentity?.accentColor ?? '#2effb8',
  );

  const purchaseNotificationForm = useForm<PurchaseNotificationFormValues>({
    resolver: zodResolver(purchaseNotificationSchema),
    defaultValues: {
      privateChunks: [{ type: 'text', value: '', id: crypto.randomUUID() }],
      privateIcon: 'award',
      displayPrivateIcon: false,
      privateBackgroundColor: 'accent',
      customPrivateBackgroundColor: '',
      privateBorderColor: 'no-border',
      customPrivateBorderColor: '',
      privateTextColor: defaultNotificationTextColor,
      privateBackgroundIcon: 'contrast',
      customPrivateBackgroundIcon: '',
      privateIconColor: 'white',
      customPrivateIconColor: '',
      publicIcon: 'award',
      displayPublicIcon: false,
      publicBackgroundColor: 'accent',
      customPublicBackgroundColor: '',
      publicBorderColor: 'no-border',
      customPublicBorderColor: '',
      publicTextColor: defaultNotificationTextColor,
      publicBackgroundIcon: 'contrast',
      customPublicBackgroundIcon: '',
      publicIconColor: 'white',
      customPublicIconColor: '',
      publicVariants: [
        {
          id: crypto.randomUUID(),
          chunks: [{ type: 'text', value: '', id: crypto.randomUUID() }],
          selectedIcon: 'award',
          showIcon: false,
        },
      ],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (
      !purchaseNotificationConfigLoading &&
      purchaseNotificationConfig &&
      purchaseNotificationConfig.webinarId === webinarId &&
      initializedWebinarId !== webinarId
    ) {
      const privateChunks = messageTemplateToChunks(
        purchaseNotificationConfig.privateMessageTemplate,
      );

      const publicVariants = [...(purchaseNotificationConfig.publicVariants || [])]
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((variant) => ({
          id: crypto.randomUUID(),
          chunks: messageTemplateToChunks(variant.messageTemplate),
          selectedIcon: purchaseNotificationConfig.publicIcon || 'award',
          showIcon: purchaseNotificationConfig.displayPublicIcon || false,
        }));

      if (publicVariants.length === 0) {
        publicVariants.push({
          id: crypto.randomUUID(),
          chunks: [{ type: 'text', value: '', id: crypto.randomUUID() }],
          selectedIcon: purchaseNotificationConfig.publicIcon || 'award',
          showIcon: purchaseNotificationConfig.displayPublicIcon || false,
        });
      }

      const cfg = purchaseNotificationConfig;

      // Sans couleur de texte enregistrée, on dérive un noir/blanc lisible du fond
      // réellement résolu (et non un blanc figé, illisible en base claire).
      const resolvedPrivateBg = resolveColorToHex(
        mapColorToId(cfg.privateBackgroundColor, 'accent', visualIdentity),
        isCustomColor(cfg.privateBackgroundColor) ? cfg.privateBackgroundColor : undefined,
        visualIdentity,
      );
      const resolvedPublicBg = resolveColorToHex(
        mapColorToId(cfg.publicBackgroundColor, 'accent', visualIdentity),
        isCustomColor(cfg.publicBackgroundColor) ? cfg.publicBackgroundColor : undefined,
        visualIdentity,
      );

      purchaseNotificationForm.reset({
        privateChunks,
        privateIcon: cfg.privateIcon || 'award',
        displayPrivateIcon: cfg.displayPrivateIcon ?? false,
        privateBackgroundColor: mapColorToId(cfg.privateBackgroundColor, 'accent', visualIdentity),
        customPrivateBackgroundColor: isCustomColor(cfg.privateBackgroundColor)
          ? cfg.privateBackgroundColor
          : '',
        privateBorderColor: isNoBorder(cfg.privateBorderColor)
          ? 'no-border'
          : mapColorToId(cfg.privateBorderColor, 'accent', visualIdentity),
        customPrivateBorderColor: isNoBorder(cfg.privateBorderColor)
          ? ''
          : isCustomColor(cfg.privateBorderColor)
            ? cfg.privateBorderColor
            : '',
        privateTextColor: cfg.privateTextColor || getReadableTextColor(resolvedPrivateBg),
        privateBackgroundIcon: mapColorToId(cfg.privateBackgroundIcon, 'contrast', visualIdentity),
        customPrivateBackgroundIcon: isCustomColor(cfg.privateBackgroundIcon)
          ? cfg.privateBackgroundIcon
          : '',
        privateIconColor: mapColorToId(cfg.privateIconColor, 'white', visualIdentity),
        customPrivateIconColor: isCustomColor(cfg.privateIconColor) ? cfg.privateIconColor : '',
        publicIcon: cfg.publicIcon || 'award',
        displayPublicIcon: cfg.displayPublicIcon ?? false,
        publicBackgroundColor: mapColorToId(cfg.publicBackgroundColor, 'accent', visualIdentity),
        customPublicBackgroundColor: isCustomColor(cfg.publicBackgroundColor)
          ? cfg.publicBackgroundColor
          : '',
        publicBorderColor: isNoBorder(cfg.publicBorderColor)
          ? 'no-border'
          : mapColorToId(cfg.publicBorderColor, 'accent', visualIdentity),
        customPublicBorderColor: isNoBorder(cfg.publicBorderColor)
          ? ''
          : isCustomColor(cfg.publicBorderColor)
            ? cfg.publicBorderColor
            : '',
        publicTextColor: cfg.publicTextColor || getReadableTextColor(resolvedPublicBg),
        publicBackgroundIcon: mapColorToId(cfg.publicBackgroundIcon, 'contrast', visualIdentity),
        customPublicBackgroundIcon: isCustomColor(cfg.publicBackgroundIcon)
          ? cfg.publicBackgroundIcon
          : '',
        publicIconColor: mapColorToId(cfg.publicIconColor, 'white', visualIdentity),
        customPublicIconColor: isCustomColor(cfg.publicIconColor) ? cfg.publicIconColor : '',
        publicVariants,
      });

      setInitializedWebinarId(webinarId);
    }
  }, [
    purchaseNotificationConfig,
    purchaseNotificationConfigLoading,
    initializedWebinarId,
    webinarId,
    visualIdentity,
    purchaseNotificationForm,
  ]);

  const privateChunks = purchaseNotificationForm.watch('privateChunks');
  const publicVariants = purchaseNotificationForm.watch('publicVariants');

  const privateChunksKey = JSON.stringify(privateChunks);
  const publicVariantsKey = JSON.stringify(publicVariants);

  const isFormValid = useMemo(() => {
    if (!hasValidContent(privateChunks)) {
      return false;
    }

    const privateMessageLength = calculateMessageLength(privateChunks);
    if (privateMessageLength > 150) {
      return false;
    }

    if (!publicVariants || publicVariants.length === 0) {
      return false;
    }

    for (const variant of publicVariants) {
      if (!hasValidContent(variant.chunks)) {
        return false;
      }

      const variantMessageLength = calculateMessageLength(variant.chunks);
      if (variantMessageLength > 150) {
        return false;
      }
    }

    return true;
  }, [privateChunksKey, publicVariantsKey]);

  const transformToApiInput = (
    data: PurchaseNotificationFormValues,
  ): WebinarPurchaseNotificationConfigInput => {
    return {
      privateMessageTemplate: chunksToMessageTemplate(data.privateChunks),
      privateIcon: data.privateIcon,
      displayPrivateIcon: data.displayPrivateIcon,
      privateBackgroundColor: resolveBackgroundColorForApi(
        data.privateBackgroundColor,
        data.customPrivateBackgroundColor,
        visualIdentity,
      ),
      privateBorderColor:
        data.privateBorderColor === 'no-border'
          ? 'transparent'
          : resolveColorToHex(
              data.privateBorderColor,
              data.customPrivateBorderColor,
              visualIdentity,
            ),
      privateTextColor: data.privateTextColor,
      privateBackgroundIcon: resolveColorToHex(
        data.privateBackgroundIcon,
        data.customPrivateBackgroundIcon,
        visualIdentity,
      ),
      privateIconColor: resolveColorToHex(
        data.privateIconColor,
        data.customPrivateIconColor,
        visualIdentity,
      ),
      publicIcon: data.publicIcon,
      displayPublicIcon: data.displayPublicIcon,
      publicBackgroundColor: resolveBackgroundColorForApi(
        data.publicBackgroundColor,
        data.customPublicBackgroundColor,
        visualIdentity,
      ),
      publicBorderColor:
        data.publicBorderColor === 'no-border'
          ? 'transparent'
          : resolveColorToHex(data.publicBorderColor, data.customPublicBorderColor, visualIdentity),
      publicTextColor: data.publicTextColor,
      publicBackgroundIcon: resolveColorToHex(
        data.publicBackgroundIcon,
        data.customPublicBackgroundIcon,
        visualIdentity,
      ),
      publicIconColor: resolveColorToHex(
        data.publicIconColor,
        data.customPublicIconColor,
        visualIdentity,
      ),
      publicVariants: data.publicVariants.map((variant, index) => ({
        messageTemplate: chunksToMessageTemplate(variant.chunks),
        position: index,
      })),
      enabled: true,
    };
  };

  const validateConfiguration = async (): Promise<boolean> => {
    const isValid = await purchaseNotificationForm.trigger();
    if (!isValid) {
      const errors = purchaseNotificationForm.formState.errors;
      console.error('❌ Validation errors:', errors);

      const errorMessages: string[] = [];

      if (errors.privateChunks) {
        errorMessages.push(t('popupForm.errors.privateChunksError'));
      }
      if (errors.publicVariants) {
        errorMessages.push(t('popupForm.errors.publicVariantsError'));
      }
      if (errors.privateIcon) {
        errorMessages.push(t('popupForm.errors.privateIconError'));
      }
      if (errors.publicIcon) {
        errorMessages.push(t('popupForm.errors.publicIconError'));
      }

      toast.error(
        errorMessages.length > 0 ? errorMessages.join(', ') : t('popupForm.errors.formErrors'),
      );
      return false;
    }
    return true;
  };

  const saveConfiguration = async (targetOfferId: number): Promise<boolean> => {
    if (locked) return false;
    if (!(await validateConfiguration())) return false;

    if (!webinarId || !workspaceId) {
      toast.error(t('popupForm.errors.missingIds'));
      console.warn('⚠️ Missing webinarId or workspaceId, cannot save');
      return false;
    }

    setIsSaving(true);

    try {
      const formData = purchaseNotificationForm.getValues();
      const apiInput = transformToApiInput(formData);

      const result = await dispatch(
        updateOfferPurchaseNotificationConfig({
          workspaceId,
          webinarId,
          offerId: targetOfferId,
          input: apiInput,
        }),
      );

      const isFulfilled = updateOfferPurchaseNotificationConfig.fulfilled.match(result);
      const isRejected = updateOfferPurchaseNotificationConfig.rejected.match(result);

      if (isFulfilled) {
        if (!hideSaveButton) toast.success(t('popupForm.errors.saveSuccess'));
        setInitializedWebinarId(undefined);
        dispatch(
          fetchOfferPurchaseNotificationConfig({ workspaceId, webinarId, offerId: targetOfferId }),
        );
        onSaveSuccess?.();
        return true;
      } else if (isRejected) {
        const appError = result.payload;
        console.error('❌ Error saving purchase notification config:', appError);

        let errorMessage = t('popupForm.errors.saveError');

        if (appError) {
          if (appError.code === 'VALIDATION') {
            errorMessage = t('popupForm.errors.validationError', {
              message: appError.message || t('popupForm.errors.invalidData'),
            });
            if (appError.details && typeof appError.details === 'object') {
              const details = appError.details as Record<string, unknown>;
              const detailMessages = Object.entries(details)
                .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('; ');
              if (detailMessages) {
                errorMessage += ' - ' + detailMessages;
              }
            }
          } else if (appError.code === 'NETWORK') {
            errorMessage = t('popupForm.errors.networkError');
          } else if (appError.code === 'UNAUTHORIZED' || appError.code === 'FORBIDDEN') {
            errorMessage = t('popupForm.errors.permissionError');
          } else if (appError.code === 'NOT_FOUND') {
            errorMessage = t('popupForm.errors.notFound');
          } else if (appError.code === 'SERVER') {
            errorMessage = t('popupForm.errors.serverError');
          } else if (appError.message) {
            errorMessage = appError.message;
          }
        }

        toast.error(errorMessage);
      }
      return false;
    } catch (error) {
      console.error('❌ Unexpected error:', error);
      const appError = normalizeAxiosError(error);
      toast.error(appError.message || t('popupForm.errors.unexpectedError'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    buildPurchaseNotificationInput: async () => {
      if (!(await validateConfiguration())) return null;
      return transformToApiInput(purchaseNotificationForm.getValues());
    },
  }));

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-0',
          locked && 'pointer-events-none select-none opacity-90',
        )}
      >
        <PopUpBuyer
          purchaseNotificationForm={purchaseNotificationForm}
          visualIdentity={visualIdentity}
          lengthFn={calculateMessageLength}
        />

        <Separator className="bg-input mt-4 mb-8" />

        <PopUpBuyer
          isPublic
          purchaseNotificationForm={purchaseNotificationForm}
          visualIdentity={visualIdentity}
          lengthFn={calculateMessageLength}
        />
      </div>

      {!hideSaveButton && (
        <Button
          className="w-full disabled:bg-gray-200 disabled:text-gray-500"
          onClick={() => {
            if (offerId != null) void saveConfiguration(offerId);
          }}
          disabled={locked || isSaving || !isFormValid || offerId == null}
          type="button"
        >
          {isSaving && <Loader />}
          {isSaving ? t('popupForm.saving') : t('popupForm.save')}
        </Button>
      )}
    </>
  );
});

export default PopUpsSection;
